/**
 * GenerateProgressButton — notch with progress ring that expands to
 * a pill button with context-sensitive label.
 *
 * States:
 *   0-2 beats → notch shape with circular progress ring
 *   3+ beats, genState idle → "Generate book" pill
 *   generating_prompts → "Creating layout..." pill with spinner
 *   generating_pages → cover / "Getting pages ready..." / "Drawing page N" pill with spinner
 *   done → "Read my book" pill
 */

import React, { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, Animated, StyleSheet, Image, ActivityIndicator } from "react-native";
import { colors, textStyle, centeredOverlay } from "../../styles";
import { useComicStore } from "../../store/comicStore";
import { getGenPhase, selectDrawingPageDone } from "../../store/comicGenStatus";
import NotchShape from "../icons/NotchShape";

const REQUIRED_STEPS = 3;
const NOTCH_W = 80;
const NOTCH_H = 124;
const RING_SIZE = 48;
const PILL_W = 231;          // base width, tuned to fit the "Generate book" label
const PILL_H = 80;
const PILL_RIGHT_GAP = 16;
const PILL_FONT = 20;
const PILL_ICON = 24;
const STATE_DELAY_MS = 800;

// Default idle label. Also the reference the pill width is tuned around, so the
// base-width measurement and the rendered default must use the same string.
const BASE_LABEL = "Generate book";

const PROGRESS_IMAGES = [
  require("../../assets/icons/progress-0.png"),
  require("../../assets/icons/progress-1.png"),
  require("../../assets/icons/progress-2.png"),
  require("../../assets/icons/progress-3.png"),
];

interface Props {
  /** Progress step: 0 = no progress, 1 = 33%, 2 = 66%, 3 = ready */
  progressStep: number;
  onPress: () => void;
  onViewBook: () => void;
  disabled?: boolean;
  scale?: (v: number) => number;
  /** Absolute vertical position of the button's top (px). When omitted, falls
   *  back to the stylesheet default (top: "62%"). */
  top?: number;
}

function ProgressRing({ step, size }: { step: number; size: number }) {
  const idx = Math.min(Math.max(step, 0), REQUIRED_STEPS);
  return (
    <Image
      source={PROGRESS_IMAGES[idx]}
      style={{ width: size, height: size }}
      resizeMode="contain"
      accessibilityLabel={`Story progress: ${idx} of ${REQUIRED_STEPS}`}
    />
  );
}

export default function GenerateProgressButton({ progressStep, onPress, onViewBook, disabled, scale: s = (v) => v, top }: Props) {
  const step = Math.min(Math.max(progressStep, 0), REQUIRED_STEPS);
  const rawReady = step >= REQUIRED_STEPS;

  const genState = useComicStore((st) => st.genState);
  const currentDrawingPage = useComicStore((st) => st.currentDrawingPage);
  const drawingPageDone = useComicStore(selectDrawingPageDone);
  const layoutComplete = useComicStore((st) => st.layoutComplete);
  const totalPages = useComicStore((st) => st.totalPages);
  const phase = getGenPhase({ genState, currentDrawingPage, drawingPageDone, layoutComplete, totalPages });

  const isGenerating = genState === "generating_prompts" || genState === "generating_pages";
  const isDone = genState === "done";
  const showPill = rawReady || isGenerating || isDone;

  // Debounce the ready state so beat animations can settle
  const [ready, setReady] = useState(showPill);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!showPill) {
      setReady(false);
    } else {
      timerRef.current = setTimeout(() => setReady(true), STATE_DELAY_MS);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [showPill]);

  // Always seed collapsed and let the mount effect animate outward, even when
  // the component mounts already-ready (e.g. returning to the screen mid-
  // generation). Seeding expanded would paint the pill at its un-grown width
  // for a frame — before the hidden mirror below has measured the label — and
  // briefly clip a long status. Expanding from the notch avoids that.
  const expandAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const anim = Animated.spring(expandAnim, {
      toValue: ready ? 1 : 0,
      useNativeDriver: false,
      friction: 8,
      tension: 40,
    });
    anim.start();
    return () => anim.stop();
  }, [ready, expandAnim]);

  // The original pill width was tuned to fit the default "Generate book" label.
  // Measure both the current label and that default reference, then grow the
  // pill only by the extra width a longer status needs — so the default stays
  // at its original size and longer labels ("Getting pages ready...") don't clip.
  const [labelWidth, setLabelWidth] = useState(0);
  const [baseLabelWidth, setBaseLabelWidth] = useState(0);
  const extra = labelWidth > 0 && baseLabelWidth > 0
    ? Math.max(0, labelWidth - baseLabelWidth)
    : 0;
  const targetPillW = s(PILL_W) + extra;

  // A single spring drives the outer width: to the notch when collapsed, and to
  // the measured pill width when expanded. This both plays the notch→pill expand
  // and grows the pill smoothly when the label changes (e.g. layout completing →
  // "Drawing page 3/12"), instead of clipping against a fixed width.
  const animWidth = useRef(new Animated.Value(s(NOTCH_W))).current;
  useEffect(() => {
    const anim = Animated.spring(animWidth, {
      toValue: ready ? targetPillW : s(NOTCH_W),
      useNativeDriver: false,
      friction: 8,
      tension: 40,
    });
    anim.start();
    return () => anim.stop();
  }, [ready, targetPillW, animWidth]);
  const animHeight = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [s(NOTCH_H), s(PILL_H)],
  });
  const pillOpacity = expandAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });
  const notchOpacity = expandAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [1, 0, 0],
  });
  const rightOffset = expandAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, s(PILL_RIGHT_GAP)],
    extrapolate: "clamp",
  });

  // Derive label and icon
  let label = BASE_LABEL;
  let showSpinner = false;
  if (phase) {
    showSpinner = true;
    switch (phase.kind) {
      case "creatingLayout": label = "Creating layout..."; break;
      case "drawingCover": label = "Drawing cover..."; break;
      case "awaitingPages": label = "Getting pages ready..."; break;
      case "drawingPage":
        label = phase.total != null ? `Drawing page ${phase.page}/${phase.total}` : `Drawing page ${phase.page}`;
        break;
    }
  } else if (isDone) {
    label = "Read my book";
  }

  const handlePress = () => {
    if (isGenerating || isDone) {
      onViewBook();
    } else {
      onPress();
    }
  };

  const a11yLabel = ready ? label : `Story progress: ${step} of ${REQUIRED_STEPS}`;

  return (
    <Animated.View style={[btnStyles.outer, top != null && { top }, { right: rightOffset }]}>
      {/* Hidden mirrors: measure the current label and the base "Generate book"
          reference so the pill grows by only the extra width longer statuses
          need. Measuring both together keeps the width delta font-consistent
          (both pick up the same web font swap). Off-screen and non-interactive. */}
      <View
        style={btnStyles.measure}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <Text
          style={[btnStyles.pillText, { fontSize: s(PILL_FONT) }]}
          numberOfLines={1}
          onLayout={(e) => setLabelWidth(e.nativeEvent.layout.width)}
        >
          {label}
        </Text>
        <Text
          style={[btnStyles.pillText, { fontSize: s(PILL_FONT) }]}
          numberOfLines={1}
          onLayout={(e) => setBaseLabelWidth(e.nativeEvent.layout.width)}
        >
          {BASE_LABEL}
        </Text>
      </View>
      <Animated.View style={[btnStyles.sizer, { width: animWidth, height: animHeight }]}>
        <Pressable
          style={btnStyles.pressable}
          onPress={handlePress}
          disabled={!ready || (disabled && !isGenerating && !isDone)}
          accessibilityLabel={a11yLabel}
          accessibilityRole="button"
        >
          <Animated.View style={[btnStyles.notchLayer, { opacity: notchOpacity }]} pointerEvents={ready ? "none" : "auto"}>
            <View style={[btnStyles.notchWrap, { width: s(NOTCH_W) }]}>
              <View style={StyleSheet.absoluteFill}>
                <NotchShape width={s(NOTCH_W)} height={s(NOTCH_H)} />
              </View>
              <ProgressRing step={step} size={s(RING_SIZE)} />
            </View>
          </Animated.View>

          <Animated.View
            style={[btnStyles.pillLayer, { opacity: pillOpacity }]}
            pointerEvents={ready ? "auto" : "none"}
          >
            {showSpinner ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Image source={require("../../assets/icons/sparkle.png")} style={{ width: s(PILL_ICON), height: s(PILL_ICON), tintColor: colors.white }} resizeMode="contain" />
            )}
            <Text style={[btnStyles.pillText, { fontSize: s(PILL_FONT) }]} numberOfLines={1}>{label}</Text>
          </Animated.View>
        </Pressable>
      </Animated.View>
    </Animated.View>
  );
}

const btnStyles = StyleSheet.create({
  outer: {
    position: "absolute",
    top: "62%",
  },
  // Off-screen self-sizing mirror used only to measure the label's natural width.
  measure: {
    position: "absolute",
    opacity: 0,
    left: -9999,
    top: 0,
  },
  sizer: {
    position: "relative",
  },
  pressable: {
    flex: 1,
    position: "relative",
  },
  notchLayer: {
    ...centeredOverlay,
    alignItems: "flex-end",
  },
  notchWrap: {
    ...centeredOverlay,
    left: undefined,
    right: 0,
  },
  pillLayer: {
    ...centeredOverlay,
    backgroundColor: colors.primary,
    borderRadius: 80,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  pillText: {
    ...textStyle.headingMedium,
    color: colors.white,
  },
});
